import React from "react";
import PropTypes from "prop-types";
import './Tabs.css';
import { useTabs } from "../../Hooks/Tabs/useTabs";

/**
 * Tabs navigation component. Can be used as:
 * 1. Controlled: Pass activeTab and setActiveTab from parent
 * 2. Uncontrolled: Pass initialTab (tab object) and Tabs manages its own state
 */
export default function Tabs({
    tabs,
    setActiveTab: setActiveTabProp,
    activeTab: activeTabProp,
    onTabChange = null,
    initialTab = null,
    idProperty = "id",
    labelProperty = "label"
}) {
    // If activeTab and setActiveTab are provided, use controlled mode
    // Otherwise, use uncontrolled mode with useTabs hook
    const isControlled = activeTabProp !== undefined || setActiveTabProp !== undefined;
    
    const { activeTab: activeTabInternal, setActiveTab: setActiveTabInternal } = useTabs(
        isControlled ? null : initialTab
    );
    
    const activeTab = isControlled ? activeTabProp : activeTabInternal;
    const setActiveTab = isControlled ? setActiveTabProp : setActiveTabInternal;
    
    function handleTabClick(tab) {
        if (!tab.disabled) {
            if (setActiveTab) {
                setActiveTab(tab);
            }
        }
        if (onTabChange) onTabChange(tab);
    }
    
    const activeTabId = activeTab ? activeTab[idProperty] : null;
    
    return (
        <ul className="nav nav-tabs nav-tabs-primary skynet-nav-tabs pl-0 pr-0 d-inline-block" role="tablist">
            {tabs.map((tab, idx) => {
                const tabId = tab[idProperty];
                const isActive = tabId === activeTabId;
                const tabLabel = tab[labelProperty] ?? `Tab ${idx + 1}`;
                return (
                    <li
                        className={`nav-item${isActive ? " active bold" : ""}${tab.disabled ? " disabled" : ""}`}
                        key={tabId}
                        role="presentation"
                    >
                        <a
                            id={`tab-${tabId}`}
                            tabIndex={tab.disabled ? -1 : 0}
                            role="tab"
                            aria-selected={isActive}
                            aria-disabled={tab.disabled ? "true" : "false"}
                            data-toggle="tab"
                            aria-controls={`tab-panel-${tabId}`}
                            onClick={() => handleTabClick(tab)}
                            className="bottom-0 left-0 normal-tab"
                        >
                            {tabLabel}
                            {tab.badge != null && <span className="ml-8">{tab.badge}</span>}
                        </a>
                    </li>
                );
            })}
        </ul>
    );
}

Tabs.propTypes = {
    tabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string,
        disabled: PropTypes.bool,
        badge: PropTypes.node
    })).isRequired,
    activeTab: PropTypes.object,
    setActiveTab: PropTypes.func,
    onTabChange: PropTypes.func,
    initialTab: PropTypes.object,
    idProperty: PropTypes.string,
    labelProperty: PropTypes.string
};
